import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Briefcase, CreditCard, Award, ClipboardList, XCircle, AlertTriangle, Scale } from 'lucide-react';

export default function Terms() {

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Terms of Service', href: '/terms' }
  ];

  const sections = [
    {
      icon: Briefcase,
      title: 'Services',
      content: 'We provide Web Design, Local SEO, and Maintenance (Care Plans). All projects are subject to these terms unless otherwise agreed in writing.'
    },
    {
      icon: CreditCard,
      title: 'Payments & Deposits',
      content: 'Standard builds require a 50% deposit to commence work, with the remaining 50% due upon completion/launch. Monthly Care Plans are billed via direct debit on the 1st of every month.'
    },
    {
      icon: Award,
      title: 'Intellectual Property',
      content: 'Unlike "rented" platforms (Wix/Squarespace), upon full payment, you (the Client) own the Intellectual Property rights to the custom HTML/CSS/JS code and design assets we create for you.'
    },
    {
      icon: ClipboardList,
      title: 'Client Responsibilities',
      content: 'You are responsible for providing text, images, and feedback in a timely manner. Delays in providing content may affect the launch date.'
    },
    {
      icon: XCircle,
      title: 'Cancellation',
      content: 'Monthly Care Plans may be cancelled with 30 days written notice. Deposits for custom builds are non-refundable once work has commenced.'
    },
    {
      icon: AlertTriangle,
      title: 'Liability',
      content: 'While we strive for 99.9% uptime and 0.5s load speeds, we cannot be held liable for third-party hosting outages or ISP issues.'
    },
    {
      icon: Scale,
      title: 'Jurisdiction',
      content: 'These terms are governed by the laws of England and Wales.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Terms of Service | SmartSite Solutions</title>
        <meta name="description" content="SmartSite Solutions trading terms for web design, local SEO and maintenance services. 50% deposit, IP ownership on completion. Governed by laws of England and Wales." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/terms" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-12"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white">
              Terms of Service.
            </h1>
            <p className="text-lg text-slate-400">
              Trading Terms for SmartSite Solutions.
            </p>
          </motion.div>

          {/* Glassmorphism Container */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative bg-white/5 backdrop-blur-xl border border-slate-700 rounded-3xl p-8 lg:p-12 overflow-hidden">
              {/* Subtle background glows */}
              <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
              
              <div className="relative space-y-10">
                {sections.map((section, index) => (
                  <motion.section
                    key={section.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/30 flex items-center justify-center">
                        <section.icon className="w-6 h-6 text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <h2 className="font-display text-xl lg:text-2xl font-bold text-white mb-3">
                          {section.title}
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                          {section.content}
                        </p>
                      </div>
                    </div>
                    {index < sections.length - 1 && (
                      <div className="mt-10 border-b border-slate-700/50" />
                    )}
                  </motion.section>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
